import React from 'react';
import type { RefObject } from 'react';

import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import Konva from 'konva';

import Tooltip from 'rc-tooltip';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/pro-solid-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

import { FlexBox, Spacer, TextInput } from '@eltoro-ui/components';
import { PhotoButton } from '@eltoro-ui/creative_generator/Components/UI/BackgroundImageTab/components';

import { useUnsplash } from 'Hooks/useUnsplash';

import type { CanvasElement } from 'Components/CanvasEditor/types';
import { getImageElementProps } from 'Components/CanvasEditor/utils';

interface ImagesProps {
  stageRef: RefObject<Konva.Stage>;
  onAddElement: (element: CanvasElement) => void;
}

export default function Images({ stageRef, onAddElement }: ImagesProps) {
  const { photos, search, handleSearch } = useUnsplash();

  const handleAddImage = async (url: string) => {
    const stage = stageRef.current;
    if (!stage) return;

    const image = await getImageElementProps({
      width: stage.width(),
      height: stage.height(),
      url,
      isAddImage: true,
    });

    onAddElement({ ...image });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;

    const [file] = files;
    const reader = new FileReader();

    reader.onload = event => {
      if (event.target && typeof event.target.result === 'string') {
        handleAddImage(event.target.result);
      }
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <FlexBox flexDirection="column">
      <FlexBox flexDirection="column" gap="0.75rem">
        <FlexBox alignItems="center" gap="0.5rem">
          <span className="section-title">Images</span>
          <Tooltip
            placement="topLeft"
            trigger="hover"
            overlayClassName={classNames('audience-tooltip', 'save-audience-info')}
            showArrow
            align={{
              offset: [-15, -3],
              targetOffset: [-6, 0],
            }}
            overlay={
              <span>
                Images provided by Unsplash. To learn more visit{' '}
                <a
                  href="https://unsplash.com/license"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'inherit' }}
                >
                  License | Unsplash
                </a>
              </span>
            }
            getTooltipContainer={() => document.body}
          >
            <span style={{ cursor: 'pointer', display: 'flex' }}>
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color="#AAB2B5"
                style={{ fontSize: 20 }}
              />
            </span>
          </Tooltip>
        </FlexBox>
        <div className="image-uploader">
          <label htmlFor="upload-image" className="upload-button">
            <FontAwesomeIcon icon={faUpload} />
            <span>Upload Image</span>
            <input
              id="upload-image"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </label>
        </div>
      </FlexBox>
      <FlexBox flexDirection="column" gap="0.75rem">
        <TextInput
          className="unsplash-image-search-input"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          icon={<FontAwesomeIcon icon={faSearch} />}
        />
        <FlexBox flexDirection="column">
          {photos.map(photo => (
            <React.Fragment key={photo.id}>
              <PhotoButton
                photo={photo}
                onClick={() => {
                  handleAddImage(photo.urls.regular);
                }}
              />
              <Spacer />
            </React.Fragment>
          ))}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}
